import { withProps } from '@udecode/cn';
import { createSlateEditor, serializeHtml, SlateLeaf } from '@udecode/plate';
import { TElement } from '@udecode/plate';
import { HEADING_KEYS } from '@udecode/plate-heading';
import {
  BasicMarksPlugin,
  BoldPlugin,
  ItalicPlugin,
  StrikethroughPlugin,
  UnderlinePlugin,
  CodePlugin,
} from '@udecode/plate-basic-marks/react';
import { BasicElementsPlugin } from '@udecode/plate-basic-elements/react';
import { HeadingPlugin } from '@udecode/plate-heading/react';
import { HighlightPlugin } from '@udecode/plate-highlight/react';
import { LinkPlugin } from '@udecode/plate-link/react';
import { ImagePlugin, MediaEmbedPlugin } from '@udecode/plate-media/react';
import { TablePlugin } from '@udecode/plate-table/react';
import { ParagraphPlugin } from '@udecode/plate/react';
import { ListPlugin } from '@udecode/plate-list/react';
import {
  ParagraphElementStatic,
  HeadingElementStatic,
  BlockquoteElementStatic,
  UnorderedListElementStatic,
  OrderedListElementStatic,
  ListItemElementStatic,
  LinkElementStatic,
  ImageElementStatic,
  MediaEmbedElementStatic,
  TableElementStatic,
  TableRowElementStatic,
  TableCellElementStatic,
  TableCellHeaderElementStatic,
  HighlightLeafStatic,
  CodeBlockElementStatic,
} from './static-components';

// Static components mapping for HTML serialization
export const htmlSerializationComponents = {
  // Paragraph
  [ParagraphPlugin.key]: ParagraphElementStatic,
  
  // Headings
  [HEADING_KEYS.h1]: withProps(HeadingElementStatic, { variant: 'h1' }),
  [HEADING_KEYS.h2]: withProps(HeadingElementStatic, { variant: 'h2' }),
  [HEADING_KEYS.h3]: withProps(HeadingElementStatic, { variant: 'h3' }),
  [HEADING_KEYS.h4]: withProps(HeadingElementStatic, { variant: 'h4' }),
  [HEADING_KEYS.h5]: withProps(HeadingElementStatic, { variant: 'h5' }),
  [HEADING_KEYS.h6]: withProps(HeadingElementStatic, { variant: 'h6' }),
  
  // Block elements
  blockquote: BlockquoteElementStatic,
  
  // Lists
  ul: UnorderedListElementStatic,
  ol: OrderedListElementStatic,
  li: ListItemElementStatic,
  
  // Text marks (using SlateLeaf with proper HTML tags)
  [BoldPlugin.key]: withProps(SlateLeaf, { as: 'strong' }),
  [ItalicPlugin.key]: withProps(SlateLeaf, { as: 'em' }),
  [StrikethroughPlugin.key]: withProps(SlateLeaf, { as: 's' }),
  [UnderlinePlugin.key]: withProps(SlateLeaf, { as: 'u' }),
  [HighlightPlugin.key]: HighlightLeafStatic,
  [CodePlugin.key]: withProps(SlateLeaf, { as: 'code' }),
  
  // Code
  code_block: CodeBlockElementStatic,
  
  // Links
  [LinkPlugin.key]: LinkElementStatic,
  
  // Media - use correct plugin keys
  img: ImageElementStatic,
  media_embed: MediaEmbedElementStatic,
  
  // Tables - use correct plugin keys
  table: TableElementStatic,
  tr: TableRowElementStatic,
  td: TableCellElementStatic,
  th: TableCellHeaderElementStatic,
};

// Options for HTML serialization
export interface SerializeToHtmlOptions {
  /** Strip CSS class names from the output */
  stripClassNames?: boolean;
  /** Strip data attributes from the output */
  stripDataAttributes?: boolean;
  /** Custom components to override defaults */
  customComponents?: Record<string, React.ComponentType<unknown>>;
  /** Include CSS styles in the HTML */
  includeStyles?: boolean;
  /** Custom CSS to include */
  customCSS?: string;
}

/**
 * Serialize PlateJS content to clean HTML
 */
export const serializeToHtml = async (
  content: TElement[],
  options: SerializeToHtmlOptions = {}
): Promise<string> => {
  const {
    stripClassNames = false,
    stripDataAttributes = false,
    customComponents = {},
  } = options;

  // Create a temporary editor for serialization with necessary plugins
  const editor = createSlateEditor({
    plugins: [
      BasicElementsPlugin,
      BasicMarksPlugin,
      HeadingPlugin,
      ListPlugin,
      HighlightPlugin,
      LinkPlugin,
      ImagePlugin,
      MediaEmbedPlugin,
      TablePlugin,
    ],
    value: content,
  });

  // Merge custom components with defaults
  const components = {
    ...htmlSerializationComponents,
    ...customComponents,
  };

  try {
    // Serialize to HTML
    const html = await serializeHtml(editor, {
      components,
      stripClassNames,
      stripDataAttributes,
    });

    return html;
  } catch (error) {
    console.error('Error serializing content to HTML:', error);
    throw new Error('Failed to serialize content to HTML');
  }
};

/**
 * Create a complete HTML document from PlateJS content
 */
export const createHtmlDocument = async (
  content: TElement[],
  options: SerializeToHtmlOptions & {
    title?: string;
    description?: string;
    viewport?: string;
    lang?: string;
  } = {}
): Promise<string> => {
  const {
    title = 'Document',
    description = '',
    viewport = 'width=device-width, initial-scale=1',
    lang = 'en',
    includeStyles = true,
    customCSS = '',
    ...serializeOptions
  } = options;

  // Get the serialized HTML content
  const bodyContent = await serializeToHtml(content, {
    ...serializeOptions,
    includeStyles: false, // We'll handle styles at document level
  });

  // Default CSS styles for clean HTML output
  const defaultCSS = includeStyles ? `
    <style>
      body {
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
        line-height: 1.6;
        color: #333;
        max-width: 800px;
        margin: 0 auto;
        padding: 20px;
      }
      
      h1, h2, h3, h4, h5, h6 {
        margin-top: 1.5em;
        margin-bottom: 0.5em;
        font-weight: 600;
      }
      
      h1 { font-size: 2.25em; }
      h2 { font-size: 1.875em; }
      h3 { font-size: 1.5em; }
      h4 { font-size: 1.25em; }
      h5 { font-size: 1.125em; }
      h6 { font-size: 1em; }
      
      p {
        margin-bottom: 1em;
      }
      
      blockquote {
        border-left: 4px solid #e5e7eb;
        padding-left: 1em;
        margin: 1em 0;
        color: #6b7280;
        font-style: italic;
      }
      
      ul, ol {
        margin: 1em 0;
        padding-left: 2em;
      }
      
      li {
        margin-bottom: 0.25em;
      }
      
      a {
        color: #3b82f6;
        text-decoration: none;
      }
      
      a:hover {
        text-decoration: underline;
      }
      
      img {
        max-width: 100%;
        height: auto;
        border-radius: 8px;
        margin: 1em 0;
      }
      
      figure {
        margin: 1.5em 0;
        text-align: center;
      }
      
      figcaption {
        font-size: 0.875em;
        color: #6b7280;
        margin-top: 0.5em;
      }
      
      table {
        width: 100%;
        border-collapse: collapse;
        margin: 1.5em 0;
        border: 1px solid #e5e7eb;
        border-radius: 8px;
        overflow: hidden;
      }
      
      th, td {
        padding: 0.75em 1em;
        text-align: left;
        border-bottom: 1px solid #e5e7eb;
      }
      
      th {
        background-color: #f9fafb;
        font-weight: 600;
      }
      
      tr:last-child td {
        border-bottom: none;
      }
      
      code {
        background-color: #f3f4f6;
        color: #374151;
        padding: 0.125em 0.25em;
        border-radius: 4px;
        font-size: 0.875em;
        font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
      }
      
      pre {
        background-color: #1f2937;
        color: #f9fafb;
        padding: 1em;
        border-radius: 8px;
        overflow-x: auto;
        margin: 1.5em 0;
      }
      
      pre code {
        background: none;
        color: inherit;
        padding: 0;
        border-radius: 0;
        font-size: inherit;
      }
      
      mark {
        background-color: #fef3c7;
        color: #92400e;
        padding: 0.125em 0.25em;
        border-radius: 4px;
      }
      
      .aspect-video {
        aspect-ratio: 16 / 9;
      }
      
      iframe {
        width: 100%;
        height: 100%;
        border: none;
        border-radius: 8px;
      }
    </style>
  ` : '';

  // Build the complete HTML document
  const htmlDocument = `<!DOCTYPE html>
<html lang="${lang}">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="${viewport}">
  <title>${title}</title>
  ${description ? `<meta name="description" content="${description}">` : ''}
  ${defaultCSS}
  ${customCSS ? `<style>${customCSS}</style>` : ''}
</head>
<body>
  ${bodyContent}
</body>
</html>`;

  return htmlDocument;
};

/**
 * Export content as downloadable HTML file
 */
export const exportAsHtmlFile = async (
  content: TElement[],
  filename: string = 'document.html',
  options: SerializeToHtmlOptions & {
    title?: string;
    description?: string;
  } = {}
): Promise<void> => {
  try {
    const htmlDocument = await createHtmlDocument(content, options);
    
    // Create blob and download
    const blob = new Blob([htmlDocument], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Clean up
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error exporting HTML file:', error);
    throw new Error('Failed to export HTML file');
  }
};

/**
 * Get HTML content for preview (without full document structure)
 */
export const getHtmlPreview = async (
  content: TElement[],
  options: SerializeToHtmlOptions = {}
): Promise<string> => {
  return serializeToHtml(content, {
    stripClassNames: true,
    stripDataAttributes: true,
    ...options,
  });
};